import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DEFAULT_STATION_PATH_TO_ZONE_CODE, DEFAULT_STATION_NAMES } from './qualityZonesConfig';

const UNASSIGNED_ID = 'unassigned';

function getStationPath(station) {
  return station.stationPath ?? station.StationPath ?? station.path ?? station.Path;
}

function getStationName(station) {
  return station.stationName ?? station.StationName ?? station.name ?? station.Name;
}

function getMachineId(station) {
  return station.machineId ?? station.MachineId ?? station.machine_id;
}

function getStationId(station) {
  return station.stationId ?? station.StationId ?? station.id ?? station.Id;
}

function getStartStage(station) {
  return station.startStage ?? station.StartStage;
}

function getCompletedStage(station) {
  return station.completedStage ?? station.CompletedStage ?? station.completeStage ?? station.CompleteStage;
}

function buildColumns(zones, stations, stationZoneMap, stageGroups) {
  const zoneIdByCode = {};
  zones.forEach(z => {
    zoneIdByCode[z.code] = z.id;
  });

  const columns = { [UNASSIGNED_ID]: [] };
  zones.forEach(zone => {
    columns[zone.id] = [];
  });

  const seenKeys = new Set();
  const place = item => {
    const zoneCode = stationZoneMap[item.path];
    const zoneId = zoneCode && zoneIdByCode[zoneCode];
    if (zoneId && columns[zoneId]) {
      columns[zoneId].push(item);
    } else {
      columns[UNASSIGNED_ID].push(item);
    }
  };

  stations.forEach(station => {
    const path = getStationPath(station);
    if (!path) return;
    seenKeys.add(path);
    place({
      path,
      name: getStationName(station) || path,
      stationId: getStationId(station),
      machineId: getMachineId(station),
      startStage: getStartStage(station),
      completedStage: getCompletedStage(station)
    });
  });

  Object.entries(stationZoneMap).forEach(([key, zoneCode]) => {
    if (seenKeys.has(key)) return;
    seenKeys.add(key);
    const zoneId = zoneIdByCode[zoneCode];
    const item = { path: key, name: DEFAULT_STATION_NAMES[key] || key, stationId: null, machineId: null, startStage: null, completedStage: null };
    if (zoneId && columns[zoneId]) {
      columns[zoneId].push(item);
    } else {
      columns[UNASSIGNED_ID].push(item);
    }
  });

  stageGroups.forEach(g => {
    if (seenKeys.has(g.name)) return;
    seenKeys.add(g.name);
    place({ path: g.name, name: g.name, stationId: null, machineId: null, startStage: null, completedStage: null });
  });

  Object.values(columns).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));
  return columns;
}

function StageChip({ item, index, showPath, showMachineId, showStageRange }) {
  return (
    <Draggable draggableId={item.path} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="quality-stage-chip"
          style={{
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: 12,
            cursor: 'grab',
            boxShadow: snapshot.isDragging ? '0 2px 6px rgba(0,0,0,0.3)' : 'none',
            ...provided.draggableProps.style
          }}
        >
          {item.name}
          {showPath && (
            <div style={{ fontSize: 10, opacity: 0.65, fontFamily: 'monospace' }}>
              {item.path} (#{item.stationId ?? '-'})
            </div>
          )}
          {showMachineId && <div style={{ fontSize: 10, opacity: 0.65, fontFamily: 'monospace' }}>Machine: {item.machineId ?? '-'}</div>}
          {showStageRange && (
            <div style={{ fontSize: 10, opacity: 0.65, fontFamily: 'monospace' }}>
              Stage: {item.startStage ?? '-'} - {item.completedStage ?? '-'}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
}

StageChip.propTypes = {
  item: PropTypes.shape({
    path: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    machineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    startStage: PropTypes.number,
    completedStage: PropTypes.number
  }).isRequired,
  index: PropTypes.number.isRequired,
  showPath: PropTypes.bool.isRequired,
  showMachineId: PropTypes.bool.isRequired,
  showStageRange: PropTypes.bool.isRequired
};

function DroppableColumn({ id, title, items, showPath, showMachineId, showStageRange, maxHeight }) {
  return (
    <div className="quality-stage-zone-column" style={{ borderRadius: 6, padding: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
      <Droppable droppableId={id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="quality-stage-zone-dropzone"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignContent: 'flex-start',
              gap: 4,
              minHeight: 48,
              maxHeight: maxHeight || undefined,
              overflowY: maxHeight ? 'auto' : undefined,
              borderRadius: 4,
              padding: 4,
              background: snapshot.isDraggingOver ? 'rgba(44,123,229,0.12)' : 'transparent'
            }}
          >
            {items.map((item, index) => (
              <StageChip
                key={`${item.path}-${item.stationId}`}
                item={item}
                index={index}
                showPath={showPath}
                showMachineId={showMachineId}
                showStageRange={showStageRange}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

DroppableColumn.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  showPath: PropTypes.bool.isRequired,
  showMachineId: PropTypes.bool.isRequired,
  showStageRange: PropTypes.bool.isRequired,
  maxHeight: PropTypes.number
};

DroppableColumn.defaultProps = {
  maxHeight: null
};

function buildMapFromColumns(columns, zones) {
  const zoneCodeById = {};
  zones.forEach(z => {
    zoneCodeById[z.id] = z.code;
  });

  const map = {};
  Object.entries(columns).forEach(([zoneId, items]) => {
    if (zoneId === UNASSIGNED_ID) return;
    const code = zoneCodeById[zoneId];
    if (!code) return;
    items.forEach(item => {
      map[item.path] = code;
    });
  });
  return map;
}

export default function QualityStageZoneModal({ show, onHide, zones, stations, stageGroups, stationZoneMap, onSave }) {
  const [columns, setColumns] = useState({ [UNASSIGNED_ID]: [] });
  const [saving, setSaving] = useState(false);
  const [showPath, setShowPath] = useState(false);
  const [showMachineId, setShowMachineId] = useState(false);
  const [showStageRange, setShowStageRange] = useState(false);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    if (show) {
      setColumns(buildColumns(zones, stations, stationZoneMap, stageGroups));
    }
  }, [show, zones, stations, stationZoneMap, stageGroups]);

  const handleDragEnd = useCallback(result => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    setColumns(prev => {
      const sourceList = [...prev[source.droppableId]];
      const [moved] = sourceList.splice(source.index, 1);
      const destList = source.droppableId === destination.droppableId ? sourceList : [...prev[destination.droppableId]];
      destList.splice(destination.index, 0, moved ?? { path: draggableId, name: draggableId });

      return {
        ...prev,
        [source.droppableId]: sourceList,
        [destination.droppableId]: destList
      };
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    await onSave(buildMapFromColumns(columns, zones));
    setSaving(false);
    onHide();
  }, [columns, zones, onSave, onHide]);

  const handleResetToDefaults = useCallback(() => {
    setColumns(buildColumns(zones, stations, DEFAULT_STATION_PATH_TO_ZONE_CODE, stageGroups));
  }, [zones, stations, stageGroups]);

  const handleCopyJson = useCallback(() => {
    const json = JSON.stringify(buildMapFromColumns(columns, zones), null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(json)
        .then(() => setCopyStatus('Copied!'))
        .catch(() => setCopyStatus('Copy failed'));
    } else {
      setCopyStatus('Clipboard unavailable');
    }
    setTimeout(() => setCopyStatus(''), 2000);
  }, [columns, zones]);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Config Zone-Stage</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}>
          <div style={{ fontSize: 12, color: '#6c757d' }}>
            Drag a station from Unassigned onto the zone it belongs to. Rejected logs are matched to a station strictly by stationId (no fallback), then plotted
            on the factory map according to this station-to-zone mapping.
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              className={`quality-view-toggle-btn ${showPath ? 'is-active' : ''}`}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
              onClick={() => setShowPath(v => !v)}
            >
              {showPath ? 'Hide Station Path / Station ID' : 'Show Station Path / Station ID'}
            </button>
            <button
              type="button"
              className={`quality-view-toggle-btn ${showMachineId ? 'is-active' : ''}`}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
              onClick={() => setShowMachineId(v => !v)}
            >
              {showMachineId ? 'Hide Machine ID' : 'Show Machine ID'}
            </button>
            <button
              type="button"
              className={`quality-view-toggle-btn ${showStageRange ? 'is-active' : ''}`}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, borderRadius: 6, cursor: 'pointer' }}
              onClick={() => setShowStageRange(v => !v)}
            >
              {showStageRange ? 'Hide Start/Complete Stage' : 'Show Start/Complete Stage'}
            </button>
          </div>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <div style={{ marginBottom: 16 }}>
            <DroppableColumn
              id={UNASSIGNED_ID}
              title={`Unassigned (${columns[UNASSIGNED_ID]?.length ?? 0})`}
              items={columns[UNASSIGNED_ID] ?? []}
              showPath={showPath}
              showMachineId={showMachineId}
              showStageRange={showStageRange}
              maxHeight={220}
            />
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 10
            }}
          >
            {zones.map(zone => (
              <DroppableColumn
                key={zone.id}
                id={zone.id}
                title={zone.label}
                items={columns[zone.id] ?? []}
                showPath={showPath}
                showMachineId={showMachineId}
                showStageRange={showStageRange}
              />
            ))}
          </div>
        </DragDropContext>
      </Modal.Body>
      <Modal.Footer>
        {copyStatus && <span style={{ fontSize: 12, color: '#6c757d' }}>{copyStatus}</span>}
        <Button variant="outline-secondary" onClick={handleCopyJson} disabled={saving}>
          Copy JSON
        </Button>
        <Button variant="outline-secondary" onClick={handleResetToDefaults} disabled={saving}>
          Reset to defaults
        </Button>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

QualityStageZoneModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  zones: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired
    })
  ).isRequired,
  stations: PropTypes.arrayOf(PropTypes.object).isRequired,
  stageGroups: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  stationZoneMap: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired
};
